import { IDatasetSchema } from './schema';

export interface ICategoryLevel {
  index: number;
  label: string;
  columnRef: string;
}

export interface IReportSegment {
  index: number;
  label: string;
  categoryLevels: ICategoryLevel[];
  descriptionColumnRef: string;
  accountCodeColumnRef: string;
  accountCodeColumnLabel: string;
}

export interface IReportLedgerType extends IReportSegment {
  filterValue: string;
}

export function isReportLedger(segment: IReportSegment): segment is IReportLedgerType {
  return 'filterValue' in segment;
}

export interface IDatasetLensColumn {
  name: string;
  label: string;
  type: string;
  semanticTypes: string[];
  visible: boolean;
  joinedDimensionId?: string;
  family?: string;
  joinable?: boolean;
}

export interface IDatasetDimensionColumn {
  name: string;
  label: string;
  type: string;
  semanticTypes: string[];
  visible: boolean;
  joinable: boolean;
}

// The lens objects returned in the /lenses endpoint have different
// data than IReportDatasetLens objects returned from /lens/:id
export interface IReportDatasetLensResponse {
  id: string;
  name: string;
  description: string;
  sourceId: string;
  semanticTypes: string[];
  types: string[];
  joinedDimensionIds: string[];
  standardType: string;
  createdAt: string;
  updatedAt: string;
  dataUpdatedAt: string;
  ingestionStatus?: string;
  visible: boolean;
  isErp?: boolean;
}

export const isErpDatasetLens = (lens: Pick<IReportDatasetLensResponse, 'isErp'>): boolean => lens.isErp === true;

// Custom (user-uploaded) datasets are identified by this standardType.
export const CUSTOM_DATASET_STANDARD_TYPE = 'urn:opengov:lenses/standard/opengov/custom/1';

// Accepts any dataset shape exposing a standardType (e.g. lens response or dataset schema).
export const isCustomDataset = (dataset: { standardType?: string | null } | null | undefined): boolean =>
  dataset?.standardType === CUSTOM_DATASET_STANDARD_TYPE;

/**
 * The versionless standard type of a COMBINED (multi-entity) lens.
 *
 * A combined lens carries the general ledger of every entity in a family. Reading one is what
 * makes a report combined, and it is the only thing that does: a parent entity's user builds both
 * ordinary and combined reports, so neither the caller, their token, nor their entity settings
 * distinguishes the two. The lens is the only discriminator there is.
 *
 * Kept versionless deliberately. Standard types are versioned (`/1`, `/2`, …) and a republish
 * bumps the suffix without changing what the lens is — pinning a version would silently
 * reclassify every combined report as an ordinary one the next time the data platform ships.
 */
export const COMBINED_ENTITY_LENS_STANDARD_TYPE = 'urn:opengov:lenses/standard/accounting/erp-gl-balances-combined';

/**
 * Whether this lens carries a whole entity family's general ledger.
 *
 * A report reading one must name the entity group it belongs to (`report.entityGroupId`), and a
 * report naming a group must read one. Either half alone renders columns from one dataset against
 * an account hierarchy from another: wrong numbers, no error.
 *
 * Matches only the published standard type. Until the data platform ships it, nothing in a lens
 * payload identifies a combined one, so this answers false everywhere and the entity-group picker
 * and merge UI stay hidden — including from manual testing. That is the deliberate trade: an
 * approximate match (`accounting/gl-accounts` was tried) also matches ordinary GL reports, and now
 * that the wizard persists `entityGroupId` from this predicate, a false positive would bind an
 * ordinary report to a group and resolve its accounts against the group's chart. Wrong numbers, no
 * error — strictly worse than a hidden picker.
 */
export const isCombinedEntityLens = (lens: { standardType?: string | null } | null | undefined): boolean => {
  if (typeof lens?.standardType !== 'string') {
    return false;
  }
  return lens.standardType.replace(/\/\d+$/, '') === COMBINED_ENTITY_LENS_STANDARD_TYPE;
};

export interface IReportDatasetLensesResponse {
  lenses: IReportDatasetLensResponse[];
  nextPage: {
    cursor: string;
    totalRecords: number;
    fullPage: boolean;
  };
}

export interface IDefaultDimension {
  semanticType: string;
  dimensionId: string;
}

export interface IReportDatasetLens extends IDatasetSchema {
  dimensions?: IReportDimension[];
}

export interface IReportDimensionColumn {
  name: string;
  label: string;
  visible: boolean;
  type: string;
  semanticTypes: string[];
  joinable?: boolean;
}

export interface IReportDimension extends IDatasetSchema {
  // Add type for date metadata when we have it
  dimensionMetadata?: IReportCoaDimensionMetadataResponse;
}

export interface IReportDimensionMetadataResponse {
  dimensionType: string;
  contentSchema: string;
  content: object;
}

export interface IReportCoaDimensionMetadataResponse extends IReportDimensionMetadataResponse {
  content: {
    chartOfAccounts: IReportCoaDimensionMetadata;
  };
}

export interface IReportCoaDimensionMetadata {
  joinColumnName: string;
  dimensionId: string;
  ledgerTypes?: IReportLedgerType[];
  segments?: IReportSegment[];
}

export interface IReportColumnValues {
  data: string[];
}

export interface IValidateReportResponse {
  result: boolean;
}

/** Where a stored column reference is used, and what it is used for. */
export interface IDatasetChangeUsage {
  location: string;
  kind:
    | 'filter'
    | 'calculation'
    | 'visualization'
    | 'detailsColumn'
    | 'sort'
    | 'grouping'
    | 'presentation'
    | 'delegate';
  /** Present when the reference resolves against a dimension rather than the dataset. */
  dimensionId?: string;
}

export interface IDatasetChangeReference {
  columnName: string;
  usages: IDatasetChangeUsage[];
}

export interface IDatasetChangeCandidate {
  columnName: string;
  label: string;
  sharedSemanticTypes: string[];
  nameSimilarity: number;
}

export interface IDatasetChangeUnresolved {
  reference: IDatasetChangeReference;
  candidates: IDatasetChangeCandidate[];
  /** Pre-selected in the mapping step when one candidate is an unambiguous match. */
  autoSelectedColumnName?: string;
}

/** A reference the swap can satisfy on its own, shown for confirmation rather than choice. */
export interface IDatasetChangeAutoResolved {
  from: string;
  to: string;
  matchedBy: 'coaRebind' | 'semanticType' | 'segmentLabel' | 'adHoc';
  usages: IDatasetChangeUsage[];
}

/** Segment filters, which have no reconciliation against the target and no query-time validation. */
export interface IDatasetChangeUnanalysable {
  kind: 'segmentFilter';
  segmentId: string;
  label: string;
  location: string;
  reason: string;
}

export interface IDatasetChangeSegmentMapping {
  label: string;
  fromAccountCodeColumnRef: string;
  toAccountCodeColumnRef: string;
  matchedBy: 'position' | 'label';
}

export interface IDatasetChangeCoaRebind {
  kind: 'none' | 'refused' | 'rebind';
  /** Set when `kind` is `refused`, e.g. an EBTO cross-family swap. */
  reason?: string;
  fromDimensionId?: string;
  toDimensionId?: string;
  segmentMapping?: IDatasetChangeSegmentMapping[];
  unresolvedSegments?: IReportSegment[];
}

/**
 * Two references that would end up on the same column. The details table aliases each select entry
 * by its column name, so duplicates make the query invalid and the report stops loading — reported
 * rather than resolved, because neither source is obviously the one to keep.
 */
export interface IDatasetChangeCollision {
  targetColumnName: string;
  fromColumnNames: string[];
}

export interface IDatasetChangePlanResponse {
  resolved: IDatasetChangeReference[];
  unresolved: IDatasetChangeUnresolved[];
  autoResolved: IDatasetChangeAutoResolved[];
  unanalysable: IDatasetChangeUnanalysable[];
  /** Optional so a response from a service build predating this field is not read as a collision. */
  collisions?: IDatasetChangeCollision[];
  /**
   * Details columns the report already duplicates. It cannot be queried today, and the swap neither
   * causes nor fixes it, so the dataset cannot be changed until the report itself is corrected.
   */
  preExistingDuplicates?: string[];
  coaRebind: IDatasetChangeCoaRebind | null;
  /** True when a reference has neither a resolution nor a candidate, or the rebind was refused. */
  blocking: boolean;
}

/** What the user decided: a target column per reference, or explicit consent to drop it. */
export interface IDatasetChangeMapping {
  replacements: Record<string, string>;
  removals?: string[];
}

/**
 * One configuration moved onto a new row. The configuration table is insert-only, so a rewrite is a
 * new row plus a repointed foreign key, and a caller holding the pre-swap id has to be told which id
 * replaced it. The entry without a `savedViewId` is the report's default configuration.
 */
export interface IDatasetChangeRewrite {
  reportConfigurationId: string;
  newReportConfigurationId: string;
  savedViewId?: string;
}

export interface IDatasetChangeApplyResponse {
  reportId: string;
  datasetId: string;
  rewritten: IDatasetChangeRewrite[];
}
